import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import axiosInstance from '../config/axios';
import { CareArea } from '../interfaces/care-area';
import { PaginationResponse } from 'interfaces/pagination-response';
import { AxiosRequestConfig } from 'axios';

/**
 * @description Care area response type
 * @type {CareAreaResponse}
 */
export type CareAreaResponse = PaginationResponse<CareArea>;

/**
 * @description Care area service
 * @type {CareAreaService}
 */
const CareAreaService = {
  /**
   * @description Create care area
   * @param {Partial<CareArea>} data - Care area data
   * @returns {Promise<CareArea>} Care area
   */
  create: (data: Partial<CareArea>) => {
    return axiosInstance.post<CareArea>('/v1/care-areas', data);
  },

  /**
   * @description Update care area
   * @param {string | number} id - Care area id
   * @param {Partial<CareArea>} data - Care area data
   * @returns {Promise<CareArea>} Care area
   */
  update: (id: string | number, data: Partial<CareArea>) => {
    return axiosInstance.patch<CareArea>(`/v1/care-areas/${id}`, data);
  },

  /**
   * @description Find care area
   * @param {string | number} id - Care area id
   * @param {AxiosRequestConfig} config - Axios request config
   * @returns {Promise<CareArea>} Care area
   */
  find: (id: string | number, config?: AxiosRequestConfig) => {
    return axiosInstance.get<CareArea>(`/v1/care-areas/${id}`, config);
  },

  /**
   * @description Delete care area
   * @param {string | number} id - Care area id
   * @returns {Promise<CareArea>} Care area
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<CareArea>(`/v1/care-areas/${id}`);
  },

  /**
   * @description Get care areas
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<CareAreaResponse>} Care area response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<CareAreaResponse>('/v1/care-areas', {
      params: queryParams,
    });
  },
};

export default CareAreaService;
